"use client"

import { Button } from "@/components/ui/button"
import { Building2, Sparkles, Users, MessageSquare, BarChart3, Globe, ChevronRight, Zap, Shield, Clock } from "lucide-react"
import Link from "next/link"
import { useState, useEffect } from "react"

type Locale = "es" | "en"

const translations = {
  es: {
    title: "CRM Médico",
    subtitle: "Gestión de Pacientes con IA",
    description: "Gestiona tu consultorio con inteligencia artificial. Agenda citas, da seguimiento a pacientes y responde mensajes de forma inteligente.",
    cta: "Iniciar Sesión",
    features: {
      ai: { title: "Respuestas con IA", desc: "Mensajes inteligentes 24/7" },
      omni: { title: "Multicanal", desc: "WhatsApp, Email, SMS" },
      analytics: { title: "Seguimiento", desc: "Historial de pacientes" },
    },
    stats: {
      leads: "Pacientes Gestionados",
      time: "Tiempo de Respuesta",
      conversion: "Citas Confirmadas",
    },
    footer: "© 2026 CRM Médico. Todos los derechos reservados.",
  },
  en: {
    title: "CRM Médico",
    subtitle: "AI-Powered Patient Management",
    description: "Manage your practice with artificial intelligence. Schedule appointments, follow up with patients and reply to messages intelligently.",
    cta: "Sign In",
    features: {
      ai: { title: "AI Replies", desc: "24/7 Smart Messaging" },
      omni: { title: "Multichannel", desc: "WhatsApp, Email, SMS" },
      analytics: { title: "Follow-up", desc: "Patient History" },
    },
    stats: {
      leads: "Patients Managed",
      time: "Response Time",
      conversion: "Confirmed Appointments",
    },
    footer: "© 2026 CRM Médico. All rights reserved.",
  },
}

export default function HomePage() {
  const [locale, setLocale] = useState<Locale>("es")
  const [mounted, setMounted] = useState(false)
  const t = translations[locale]

  useEffect(() => {
    setMounted(true)
    // Check browser language
    const browserLang = navigator.language.startsWith("es") ? "es" : "en"
    const saved = localStorage.getItem("locale") as Locale
    setLocale(saved || browserLang)
  }, [])

  const toggleLocale = () => {
    const newLocale = locale === "es" ? "en" : "es"
    setLocale(newLocale)
    localStorage.setItem("locale", newLocale)
  }

  if (!mounted) return null

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-violet-50/30 dark:from-slate-950 dark:via-blue-950/20 dark:to-violet-950/20 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
      
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleLocale}
          className="gap-2 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all"
        >
          <Globe className="h-4 w-4" />
          {locale === "es" ? "🇲🇽 Español" : "🇺🇸 English"}
        </Button>
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-4 md:p-8">
        <div className="text-center max-w-2xl space-y-6">
          {/* Logo */}
          <div className="relative mx-auto">
            <div className="absolute inset-0 w-24 h-24 mx-auto bg-primary/20 rounded-3xl blur-xl animate-pulse" />
            <div className="relative mx-auto w-24 h-24 rounded-3xl bg-gradient-to-br from-primary to-violet-600 flex items-center justify-center shadow-2xl shadow-primary/30">
              <Building2 className="h-12 w-12 text-white" />
            </div>
          </div>

          {/* Title */}
          <div className="space-y-3">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-primary to-violet-600 dark:from-white dark:via-primary dark:to-violet-400 bg-clip-text text-transparent">
              {t.title}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground font-medium">
              {t.subtitle}
            </p>
          </div>

          {/* Description */}
          <p className="text-muted-foreground text-lg max-w-lg mx-auto leading-relaxed">
            {t.description}
          </p>

          {/* Features */}
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
            <div className="group p-4 rounded-2xl bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm border border-white/50 dark:border-slate-800/50 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Sparkles className="h-5 w-5 text-blue-500" />
              </div>
              <p className="font-semibold text-sm">{t.features.ai.title}</p>
              <p className="text-xs text-muted-foreground">{t.features.ai.desc}</p>
            </div>
            <div className="group p-4 rounded-2xl bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm border border-white/50 dark:border-slate-800/50 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <MessageSquare className="h-5 w-5 text-green-500" />
              </div>
              <p className="font-semibold text-sm">{t.features.omni.title}</p>
              <p className="text-xs text-muted-foreground">{t.features.omni.desc}</p>
            </div>
            <div className="group p-4 rounded-2xl bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm border border-white/50 dark:border-slate-800/50 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <BarChart3 className="h-5 w-5 text-violet-500" />
              </div>
              <p className="font-semibold text-sm">{t.features.analytics.title}</p>
              <p className="text-xs text-muted-foreground">{t.features.analytics.desc}</p>
            </div>
          </div>

          {/* CTA Button - más cerca del contenido */}
          <Link href="/login" className="block">
            <Button size="lg" className="w-full max-w-sm mx-auto gap-2 h-14 text-lg shadow-xl shadow-primary/25 hover:shadow-2xl hover:shadow-primary/30 transition-all hover:-translate-y-0.5 bg-gradient-to-r from-primary to-violet-600 hover:from-primary/90 hover:to-violet-600/90">
              {t.cta}
              <ChevronRight className="h-5 w-5" />
            </Button>
          </Link>

          {/* Trust badges - justo debajo del botón */}
          <div className="flex items-center justify-center gap-6 text-muted-foreground">
            <div className="flex items-center gap-1.5 text-xs">
              <Shield className="h-4 w-4 text-green-500" />
              <span>SSL Secure</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <Zap className="h-4 w-4 text-amber-500" />
              <span>99.9% Uptime</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <Clock className="h-4 w-4 text-blue-500" />
              <span>24/7 Support</span>
            </div>
          </div>

          {/* Stats - al final como sección secundaria */}
          <div className="pt-6 border-t border-border/50">
            <div className="flex items-center justify-center gap-8 text-center">
              <div>
                <p className="text-2xl md:text-3xl font-bold text-primary">10K+</p>
                <p className="text-xs text-muted-foreground">{t.stats.leads}</p>
              </div>
              <div className="w-px h-10 bg-border" />
              <div>
                <p className="text-2xl md:text-3xl font-bold text-green-500">&lt;2min</p>
                <p className="text-xs text-muted-foreground">{t.stats.time}</p>
              </div>
              <div className="w-px h-10 bg-border" />
              <div>
                <p className="text-2xl md:text-3xl font-bold text-violet-500">35%</p>
                <p className="text-xs text-muted-foreground">{t.stats.conversion}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="absolute bottom-4 text-xs text-muted-foreground">
          {t.footer}
        </p>
      </div>
    </div>
  )
}
